import { gql, useMutation } from "@apollo/client";

import {
  AddDeviceInput,
  BatchAddDevicesMutation,
  BatchAddDevicesMutationVariables,
} from "../../../API";
import { BATCH_ADD_DEVCIES } from "../../../common/operations/mutations";
import { handleApolloError } from "../../../common/utils/handleApolloError";

export const useCreateBatchDevices = () => {
  const [saveBatchCreateDevices, { data, loading }] = useMutation<
    BatchAddDevicesMutation,
    BatchAddDevicesMutationVariables
  >(BATCH_ADD_DEVCIES);

  const batchCreateDevices = async (input: AddDeviceInput[]) => {
    return await saveBatchCreateDevices({
      variables: {
        input,
      },
      awaitRefetchQueries: true,
      onError: handleApolloError,
      update: async (cache, response) => {
        cache.modify({
          fields: {
            getDevices(existing) {
              const newDevicesRefs =
                response?.data?.batchAddDevices?.map(d => {
                  return cache.writeFragment({
                    data: d,
                    fragment: gql`
                      fragment NewDevice on Device {
                        customerId
                        deviceData
                        id
                        cameraIpAddress
                        locationId
                        locationName
                        makeModelId
                        name
                        nodeId
                        nodeName
                        status
                      }
                    `,
                  });
                }) ?? [];

              if (!existing) {
                return response?.data;
              }

              // Filter out duplicates by checking __ref
              const existingRefs = new Set(
                existing.items?.map(
                  (item: { __ref?: string }) => item?.__ref
                ) ?? []
              );
              const uniqueNewRefs = newDevicesRefs.filter(
                ref => ref?.__ref && !existingRefs.has(ref.__ref)
              );

              return {
                ...existing,
                items: [...existing.items, ...uniqueNewRefs],
              };
            },
          },
        });
      },
    });
  };

  return {
    batchCreateDevices,
    data,
    loading,
  };
};
